import { type QueryObserverResult } from '@tanstack/react-query'
import { useMemo } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useGetMemberOnboardingInNetworkVerification } from 'utils/web_api'
import { type MemberOnboardingInNetworkVerificationRecord, type MemberOnboardingInNetworkVerificationRecordInNetworkVerificationProgressStatus, type MemberOnboardingInNetworkVerificationTask } from 'utils/web_api.schemas'

import { useOnboarding } from '../OnboardingContext'

export type InNetworkVerificationStatusType = {
  records: MemberOnboardingInNetworkVerificationRecord[],
  verifyingInNetworkStatusPlanIds: string[],
  verifyingInNetworkStatusProviderNpis: number[],
  inNetworkVerificationCreatedAt?: Date,
  inNetworkVerificationProgressStatus?: MemberOnboardingInNetworkVerificationRecordInNetworkVerificationProgressStatus,
  error?: GrapeAPIErrorType | null,
  refetchInNetworkStatus?: () => Promise<QueryObserverResult<MemberOnboardingInNetworkVerificationTask, GrapeAPIErrorType>>,
}

export const useInNetworkVerificationStatus = (): InNetworkVerificationStatusType => {
  const { isFerryVerificationTasksEnabled } = useOnboarding()
  const { data, error, refetch } = useGetMemberOnboardingInNetworkVerification<MemberOnboardingInNetworkVerificationTask, GrapeAPIErrorType>({
    query: {
      enabled: !!isFerryVerificationTasksEnabled
    }
  })
  return useMemo(() => {
    const verifyingInNetworkStatusPlanIds = data?.plan_ids ?? data?.data?.map(record => record.plan_id) ?? []
    const verifyingInNetworkStatusProviderNpis = data?.provider_npis ?? data?.data?.map(record => record.npi) ?? []
    const inNetworkVerificationProgressStatus = data?.in_network_verification_progress_status
    const inNetworkVerificationCreatedAt = data?.newest_verify_task_created_at ? new Date(data?.newest_verify_task_created_at) : undefined

    return {
      records: data?.data ?? [],
      verifyingInNetworkStatusPlanIds,
      verifyingInNetworkStatusProviderNpis,
      inNetworkVerificationProgressStatus,
      inNetworkVerificationCreatedAt,
      error,
      refetchInNetworkStatus: refetch,
    }
  }, [data, error, refetch])
}
